import { isStockPack } from './stockPack';

const isBinUnit = (value) => String(value || "").trim().toLowerCase() === "bin";
const isGranelUnit = (value) => String(value || "").trim().toLowerCase() === "granel";

export const getStandardBinPurchaseGuidance = ({ isImportList = false, metricUnit } = {}) => {
  if (isImportList || !isBinUnit(metricUnit)) return null;
  return {
    amount:
      "Cantidad = número de bins. Los montos con y sin IVA corresponden a un solo bin, no al total de la compra.",
    weightLabel: "Peso bruto por bin (kg)",
    weight:
      "Ingresa el peso bruto de un solo bin en kilos enteros. Los kilos totales se calculan como cantidad de bins × peso bruto por bin y se usan para obtener el costo por kg.",
  };
};

export const getStandardBinWeightError = ({
  isImportList = false,
  metricUnit,
  weight,
} = {}) => {
  if (isImportList || !isBinUnit(metricUnit)) return "";
  const parsedWeight = Number(weight);
  if (String(weight ?? "").trim() === ""
    || !Number.isFinite(parsedWeight)
    || parsedWeight <= 0
    || !Number.isInteger(parsedWeight)) {
    return "Ingresa el peso bruto por bin como un número entero mayor a 0 kg.";
  }
  return "";
};

export const isStandardGranelPurchase = ({ isImportList = false, metricUnit, purchaseMode } = {}) => (
  !isImportList && (purchaseMode ? purchaseMode === 'granel' : isGranelUnit(metricUnit))
);

export const getStandardGranelPurchaseGuidance = ({
  isImportList = false,
  metricUnit,
  purchaseMode,
} = {}) => {
  if (!isStandardGranelPurchase({ isImportList, metricUnit, purchaseMode })) return null;
  return {
    amount:
      "En granel, ingresa el monto total de la compra con y/o sin IVA. No se usa una cantidad adicional.",
    weightLabel: "Peso total comprado (kg)",
    weight:
      "El costo por kg se calcula dividiendo el monto total de la compra más los gastos asignados por este peso.",
  };
};

export const getStandardStockPurchaseGuidance = (options = {}) => (
  (!options.isImportList && ['pack', 'other'].includes(options.purchaseMode) ? {
    amount: 'Cantidad = número de formatos comprados. Ingresa los montos con y sin IVA por un solo formato.',
    weightLabel: 'Peso por formato (kg)',
    weight: 'Los kilos totales se calculan como cantidad de formatos × peso registrado por formato.',
  } : null)
  || (isStockPack(options.metricUnit) ? {
    amount: 'Cantidad = número de packs. Ingresa los montos con y sin IVA por un solo pack. Para una docena, ingresa 12 unidades por pack.',
    weightLabel: 'Peso por pack (kg)',
    weight: 'Si registras un peso, ingresa los kilos de un pack completo.',
  } : null)
  || getStandardBinPurchaseGuidance(options)
  || getStandardGranelPurchaseGuidance(options)
);

export const getStandardGranelWeightError = ({
  isImportList = false,
  metricUnit,
  weight,
  purchaseMode,
} = {}) => {
  if (!isStandardGranelPurchase({ isImportList, metricUnit, purchaseMode })) return "";
  const parsedWeight = Number(weight);
  if (String(weight ?? "").trim() === ""
    || !Number.isFinite(parsedWeight)
    || parsedWeight <= 0) {
    return "Ingresa el peso total comprado como un número mayor a 0 kg.";
  }
  return "";
};

export const getStandardStockWeightError = (options = {}) => (
  (['pack', 'other'].includes(options.purchaseMode) ? '' : getStandardBinWeightError(options))
  || getStandardGranelWeightError(options)
);

export const resolveStandardStockQuantity = ({
  isImportList = false,
  metricUnit,
  quantity,
  purchaseMode,
} = {}) => (
  isStandardGranelPurchase({ isImportList, metricUnit, purchaseMode }) ? 1 : quantity
);

// These scopes describe the stored purchase inputs, independently of the unit
// used to sell the product. Keep them alongside the stock calculation rules.
export const stockPurchaseUnitLabel = (value) => {
  const unit = String(value || '').trim();
  return { cajonmadera: 'cajón de madera', cajonnegro: 'cajón negro', cajonblanco: 'cajón blanco' }[unit.toLowerCase()] || unit;
};

export const getStockInputScope = ({ isImportList = false, metricUnit, purchaseMode } = {}, field) => {
  const unit = stockPurchaseUnitLabel(metricUnit) || 'unidad de compra';
  const granel = isStandardGranelPurchase({ isImportList, metricUnit, purchaseMode });
  const kg = !['pack', 'other'].includes(purchaseMode) && /^(kg|kilos?|kilogramos?)$/i.test(unit);
  const perUnit = granel ? 'Total de esta compra' : `Por ${unit}`;
  if (['price', 'priceWithoutIva'].includes(field)) return perUnit;
  if (field === 'quantity') {
    if (['pack', 'other'].includes(purchaseMode)) return 'Número de formatos comprados';
    const plural = { bin: 'bins', caja: 'cajas', saco: 'sacos', malla: 'mallas', gamela: 'gamelas', balde: 'baldes', unidad: 'unidades', 'cajón de madera': 'cajones de madera', 'cajón negro': 'cajones negros', 'cajón blanco': 'cajones blancos' }[unit.toLowerCase()];
    return granel ? 'Una compra completa' : kg ? 'Kilos comprados' : plural ? `Número de ${plural}` : `Cantidad comprada en ${unit}`;
  }
  if (field === 'weight' || field === 'weightKg') {
    if (granel) return 'Kilos totales de esta compra';
    if (!isImportList && kg) return 'La cantidad comprada determina los kilos totales';
    if (isImportList && field === 'weight') return `Kg por ${unit}; se usa si no hay peso normalizado`;
    return `Kg por ${unit}${isImportList && field === 'weightKg' ? '; tiene prioridad sobre el peso declarado' : ''}`;
  }
  if (['lengthCm', 'widthCm', 'heightCm'].includes(field)) return `Centímetros por ${unit}`;
  if (field === 'bultosPerBin') return 'Número de bultos contenidos en un solo bin';
  if (field === 'unitsPerPack') return 'Unidades contenidas en un pack; por ejemplo, 12 para una docena';
  if (field === 'metricUnit') return 'Unidad en la que se realizó la compra; puede diferir del formato de venta';
  return '';
};

export const STOCK_AMOUNT_GUIDANCE = 'Los montos con IVA y sin IVA son porciones distintas de la compra, no dos versiones del mismo monto. Se suman; para calcular el costo se agrega un 19% a la porción sin IVA.';
